import { format, isValid, parse, startOfDay } from 'date-fns';

/**
 * Value format for `dateRange` filters: a single 'yyyy-MM-dd_yyyy-MM-dd' string.
 *
 * The underscore matches the format the filters-v1 date range picker has always
 * persisted (invoices, bills, overheads, generate pages) — one app-wide codec,
 * one URL format. The separator MUST stay comma-free: the planner's URL
 * hydration splits any param containing a comma into a string[], which would
 * silently change the value shape on reload.
 */
export const DATE_RANGE_FILTER_VALUE_SEPARATOR = '_';

/** Early planner builds persisted ':' — accepted on parse for backward compatibility */
const LEGACY_SEPARATOR = ':';

const DATE_RANGE_FILTER_DATE_FORMAT = 'yyyy-MM-dd';
const DATE_PART_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export interface DateRangeFilterValue {
  startDate: Date;
  endDate: Date;
}

export interface DateRangeFilterValueStrings {
  /** Inclusive start, plain 'YYYY-MM-DD' calendar date */
  startDate: string;
  /** Inclusive end, plain 'YYYY-MM-DD' calendar date */
  endDate: string;
}

/** Serializes a date range to 'yyyy-MM-dd:yyyy-MM-dd' (local calendar dates, timezone-safe). */
export function serializeDateRangeFilterValue(range: DateRangeFilterValue): string {
  return [
    format(range.startDate, DATE_RANGE_FILTER_DATE_FORMAT),
    format(range.endDate, DATE_RANGE_FILTER_DATE_FORMAT),
  ].join(DATE_RANGE_FILTER_VALUE_SEPARATOR);
}

/**
 * Parses a serialized value into plain 'YYYY-MM-DD' strings.
 * Returns null for malformed or inverted (start > end) input — hand-edited
 * URLs must degrade to "no filter", never crash.
 */
export function parseDateRangeFilterValueStrings(
  value: string | undefined
): DateRangeFilterValueStrings | null {
  if (!value) return null;

  const separator = value.includes(DATE_RANGE_FILTER_VALUE_SEPARATOR)
    ? DATE_RANGE_FILTER_VALUE_SEPARATOR
    : LEGACY_SEPARATOR;
  const parts = value.split(separator);
  if (parts.length !== 2) return null;

  const [startDate, endDate] = parts;
  if (!DATE_PART_PATTERN.test(startDate) || !DATE_PART_PATTERN.test(endDate)) return null;

  const start = parse(startDate, DATE_RANGE_FILTER_DATE_FORMAT, new Date());
  const end = parse(endDate, DATE_RANGE_FILTER_DATE_FORMAT, new Date());
  if (!isValid(start) || !isValid(end) || start > end) return null;

  return { startDate, endDate };
}

/** Parses a serialized value into local-midnight Dates. Null on malformed/inverted input. */
export function parseDateRangeFilterValue(value: string | undefined): DateRangeFilterValue | null {
  const strings = parseDateRangeFilterValueStrings(value);
  if (!strings) return null;

  return {
    startDate: startOfDay(parse(strings.startDate, DATE_RANGE_FILTER_DATE_FORMAT, new Date())),
    endDate: startOfDay(parse(strings.endDate, DATE_RANGE_FILTER_DATE_FORMAT, new Date())),
  };
}
